import React from "react";
import { Alert, Box, Button, Drawer, Skeleton, Typography } from "@mui/material";
import { useGetSyncResultQuery } from "state/rtkQuery/sync/syncQueries";

import { dataDays, formatDuration, recordAsTask } from "./historyFilters";
import { formatDateTime, normalizeApiError } from "./syncFormat";
import { taskLabel } from "./syncNames";
import { CallerCell, HistoryStatusPill, JsonBlock, mono, OutcomePill, QueueName } from "./SyncParts";
import { useTaskOutcome } from "./useTaskOutcome";

const Row = ({ label, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, gap: 2, fontSize: 13 }}>
    <span style={{ color: "#64748b" }}>{label}</span>
    <b style={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{children}</b>
  </Box>
);

// One stored run. Older records can lack fields, so every value is shown only when it is there and never made up.
const RunDetailDrawer = ({ taskId, onClose }) => {
  const { data: record, isFetching, isError, error } = useGetSyncResultQuery({ taskId });
  const normalized = isError ? normalizeApiError(error) : null;
  const task = record ? recordAsTask(record) : null;
  const outcome = useTaskOutcome(task);
  const scope = record?.payload?.monitoring_scope;

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 580 } } }}>
      <Box sx={{ p: "18px 22px", borderBottom: "1px solid #e2e8f0" }}>
        <Typography sx={{ color: "#6a9a1e", fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>Run</Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.25, wordBreak: "break-word" }}>{record ? taskLabel(record.task_name) : "Stored run"}</Typography>
        {record?.task_name && <Typography sx={{ ...mono, color: "#94a3b8" }}>{record.task_name}</Typography>}
        <Typography sx={{ ...mono, mt: 0.5 }}>{taskId}</Typography>
      </Box>

      <Box sx={{ p: "20px 22px", overflow: "auto", flex: 1 }}>
        {isFetching && !record && (
          <>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rounded" height={120} sx={{ my: 1 }} />
          </>
        )}
        {isError && (
          <Alert severity={normalized.status === 404 ? "info" : "error"}>
            {normalized.status === 404 ? "No retained record for this task. It may be older than what is kept, or never have been stored." : normalized.message}
          </Alert>
        )}

        {record && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }} data-testid="run-detail-status">
              <HistoryStatusPill status={record.status} />
              <OutcomePill task={task} />
            </Box>

            <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", p: "8px 14px", mb: 2 }}>
              <Row label="Triggered by"><CallerCell source={record} /></Row>
              {record.owner && <Row label="Owner ID"><span style={mono} data-testid="owner-id">{record.owner}</span></Row>}
              {dataDays(record) && <Row label="Data processed"><span data-testid="data-days">{dataDays(record)}</span></Row>}
              <Row label="Submitted">{formatDateTime(record.submitted_at)}</Row>
              {record.started_at && <Row label="Started">{formatDateTime(record.started_at)}</Row>}
              {record.completed_at && <Row label="Completed">{formatDateTime(record.completed_at)}</Row>}
              {record.duration_seconds !== undefined && record.duration_seconds !== null && <Row label="Duration">{formatDuration(record.duration_seconds)}</Row>}
              {record.retry_count !== undefined && record.retry_count !== null && <Row label="Retries">{record.retry_count}</Row>}
              {record.retry_attempts ? <Row label="Retry attempts">{record.retry_attempts}</Row> : null}
              {record.platform && <Row label="Platform"><span style={mono}>{String(record.platform)}</span></Row>}
              {record.queue && <Row label="Queue"><QueueName queue={record.queue} /></Row>}
              {record.worker_hostname && <Row label="Worker"><span style={mono}>{record.worker_hostname}</span></Row>}
            </Box>

            {outcome?.kind === "skipped" && <Alert severity="warning" sx={{ mb: 2 }}>It finished, but skipped the work{record.result_summary?.reason ? `: ${record.result_summary.reason}` : ""}.</Alert>}
            {outcome?.kind === "problem" && <Alert severity="error" sx={{ mb: 2 }}>The worker finished but reported a problem.</Alert>}
            {outcome?.kind === "dispatched" && <Alert severity="info" sx={{ mb: 2 }}>Dispatch finished. The follow-up jobs it queued may still be running.</Alert>}
            {record.error && <Alert severity="error" sx={{ mb: 2 }} data-testid="run-detail-error">{typeof record.error === "string" ? record.error : JSON.stringify(record.error)}</Alert>}

            {scope && Object.keys(scope).length > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>What was asked for</Typography>
                <Typography sx={{ color: "#64748b", fontSize: 12, mb: 0.75 }}>The options chosen when the run was started. Dates here are the days of data, not when it ran.</Typography>
                <Box sx={{ mb: 2 }} data-testid="run-detail-scope"><JsonBlock value={scope} /></Box>
              </>
            )}
            {(record.payload?.args?.length > 0 || (record.payload?.kwargs && Object.keys(record.payload.kwargs).length > 0)) && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>What the worker ran with</Typography>
                <Box sx={{ mb: 2 }}><JsonBlock value={{ args: record.payload.args ?? [], kwargs: record.payload.kwargs ?? {} }} /></Box>
              </>
            )}
            {record.result_summary && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>Result</Typography>
                <Box sx={{ mb: 2 }}><JsonBlock value={record.result_summary} /></Box>
              </>
            )}

            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.75 }}>Raw record</Typography>
            <JsonBlock value={record} />
          </>
        )}
      </Box>

      <Box sx={{ p: "14px 22px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", background: "#fafbfc" }}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Drawer>
  );
};

export default RunDetailDrawer;
